import { useEffect, useState } from 'react'
import { DEFAULT_RIC_LOCATION, isPhone } from '../common/constants'
import { TEMP_MAP_CENTER } from '../common/data'
import { Map } from '../components'
import { useAccountContext } from '../contexts'
import { useDeviceUrlSync } from '../hooks/useDeviceUrlSync'

export function MapPage() {
  const { devices, selectedDevice, selectDevice, selectDeviceLocally } = useAccountContext()

  // Use the URL sync hook to handle sigfoxId query parameter
  useDeviceUrlSync()

  const [focus, setFocus] = useState(TEMP_MAP_CENTER)

  // When a marker is clicked, drive the selection (and the smooth pan) exactly as
  // before through selectDeviceLocally, then quietly reflect it in the URL with
  // replaceState. We deliberately avoid react-router navigate() here: navigating
  // re-runs useDeviceUrlSync's useSearchParams subscription and re-renders the map
  // mid-pan (recreating every marker), which makes the pan janky. replaceState
  // updates the address bar for refresh-persistence without triggering that render.
  // Preserve any other query params (e.g. from/to time range).
  const handleMarkerClick = (sigfoxId: string) => {
    selectDeviceLocally(sigfoxId)

    const params = new URLSearchParams(window.location.search)
    params.set('sigfoxId', sigfoxId)
    window.history.replaceState(null, '', `/map?${params.toString()}`)
  }

  // On desktop the map opens with no device selected — but only clear when the URL
  // doesn't already name one. Otherwise this races useDeviceUrlSync (which restores
  // the device from ?sigfoxId on load) and wins, wiping the selection on refresh /
  // direct links so the map never focuses the linked device.
  useEffect(() => {
    const urlHasDevice = /[?&]sigfoxId=[0-9A-Fa-f]{6}/.test(window.location.search)
    if (!isPhone && !urlHasDevice) selectDevice('')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Drive the map focus from the current selection. This depends on `devices` as
  // well as `selectedDevice`: on refresh / direct link the device id is restored
  // from ?sigfoxId before the devices data finishes loading, so devices[selectedDevice]
  // is briefly undefined. Without `devices` in the deps the effect would run once,
  // fall through to the Hazen default, and never re-run once the device's
  // coordinates arrive — leaving the map stuck on Hazen instead of the linked marker.
  useEffect(() => {
    let active = true
    if (selectedDevice !== '') {
      // A device is selected — center on it once its coordinates are available.
      // While they're still loading, leave the focus untouched (don't snap to
      // Hazen) so we pan straight to the marker the moment data arrives.
      const device = devices[selectedDevice]
      if (device) {
        const { coordinateNSValue, coordinateEWValue } = device
        if (coordinateNSValue && coordinateEWValue) {
          const location = { lat: coordinateNSValue, lng: coordinateEWValue }
          if (active) {
            setFocus(location)
          }
        }
      }
    } else {
      // No device selected — default to Hazen.
      if (active) {
        setFocus(DEFAULT_RIC_LOCATION)
      }
    }

    return () => {
      active = false
    }
  }, [selectedDevice, devices]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full w-full">
      <Map focus={focus} selectDevice={handleMarkerClick} />
    </div>
  )
}
